import React, { useState } from 'react';
import { getApiBaseUrl } from '../config/api';
import Snackbar from './Snackbar';
import FlashMessage from './FlashMessage';
import KycLegalTypePicker from './KycLegalTypePicker';
import './AgentRegistration.css';

const AgentRegistration = ({ onRegistrationSuccess, onBackToLogin }) => {
  const [step, setStep] = useState(1); // 1: Verify IRA, 2: Tax PIN + Details + Create Account
  const [iraNumber, setIraNumber] = useState('');
  const [kraPin, setKraPin] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [email, setEmail] = useState('');
  const [kycLegalType, setKycLegalType] = useState('');
  const [kycBusinessName, setKycBusinessName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [iraData, setIraData] = useState(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState('error');
  const [accountAlreadyExists, setAccountAlreadyExists] = useState(false);

  const handleIRAInputChange = (e) => {
    setIraNumber(e.target.value.toUpperCase());
    setError('');
  };

  const handleVerifyIRA = async (e) => {
    e.preventDefault();
    if (!iraNumber.trim()) {
      setError('Please enter your IRA number');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const API_BASE_URL = getApiBaseUrl();
      const response = await fetch(`${API_BASE_URL}/api/agent/verify-ira`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ iraNumber: iraNumber.trim() }),
      });
      const data = await response.json();
      if (!response.ok) {
        if (data.alreadyVerified) {
          setError('This IRA number is already registered. Please login with your existing credentials.');
          return;
        }
        throw new Error(data.error || 'Failed to verify IRA number');
      }
      if (data.success) {
        setIraData(data.data);
        if (data.data?.agencyName && !kycBusinessName) {
          setKycBusinessName(data.data.agencyName);
        }
        const inferred = String(data.data?.entityType || '').toLowerCase();
        if (!kycLegalType) {
          if (/(sole|individual|person|proprietor)/.test(inferred)) setKycLegalType('sole_proprietor');
          else if (/(compan|limited|ltd|corp|partner|plc|llc|business|corporate)/.test(inferred)) setKycLegalType('business_entity');
        }
        setStep(2);
      } else {
        throw new Error(data.error || 'Verification failed');
      }
    } catch (err) {
      setError(err.message || 'Failed to verify IRA number. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setError('');
    setAccountAlreadyExists(false);
    if (!kraPin.trim()) {
      setError('Please enter your Tax PIN (KRA PIN).');
      return;
    }
    if (kraPin.trim().length < 10) {
      setError('Tax PIN should be at least 10 characters (e.g. P051371296E).');
      return;
    }
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    if (!idNumber || !idNumber.trim()) {
      setError('Please enter your National ID.');
      return;
    }
    const idLen = idNumber.trim().length;
    if (idLen < 6 || idLen > 8) {
      setError('National ID must be 6 to 8 digits.');
      return;
    }
    if (!kycLegalType) {
      setError('Select whether you are a sole proprietor or a business entity.');
      return;
    }
    if (kycLegalType === 'business_entity' && !String(kycBusinessName || '').trim()) {
      setError('Enter the name of the business as it appears on your certificate.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    setIsLoading(true);
    try {
      const API_BASE_URL = getApiBaseUrl();
      const response = await fetch(`${API_BASE_URL}/api/agent/complete-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          iraNumber: iraData.iraNumber,
          email: email.trim().toLowerCase(),
          idNumber: (idNumber && idNumber.trim()) || '',
          kraPin: kraPin.trim(),
          fullNames: '', // optional; backend uses agency name from IRA when empty
          kycLegalType,
          kycBusinessName: kycLegalType === 'business_entity' ? kycBusinessName.trim() : '',
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        if (data.code === 'AGENT_ALREADY_EXISTS' || (data.error && data.error.includes('already exists for this IRA number'))) {
          setAccountAlreadyExists(true);
          setError(data.error || 'An account already exists for this IRA number. Please login with your existing credentials.');
          return;
        }
        if (data.code === 'AGENCY_NOT_FOUND') {
          setError(data.error || 'No agency record found for this Tax PIN.');
          const agencyName = (data.agencyName || iraData?.agencyName || '').trim();
          const taxPin = (data.taxPin || kraPin || '').trim();
          const subject = `Agency details check - ${agencyName || 'Agency'}`;
          const body = `Please check agency details for:\n\nAgency Name: ${agencyName}\nTax PIN (KRA PIN): ${taxPin}`;
          const mailtoUrl = `mailto:creditcontrol@monarchinsurance.co.ke?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
          setSnackbarMessage(
            <>
              No agency record found for this Tax PIN. Please email{' '}
              <a href={mailtoUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'underline' }}>
                creditcontrol@monarchinsurance.co.ke
              </a>{' '}
              with your agency name and Tax PIN so the team can check your details.
            </>
          );
          setSnackbarType('error');
          setShowSnackbar(true);
          return;
        }
        throw new Error(data.error || 'Failed to create account');
      }
      if (data.success) {
        setRegistrationSuccess(true);
        setSnackbarType('success');
        setSnackbarMessage('Application successful. Welcome to AgentBima — your login details have been sent to your email.');
        setShowSnackbar(true);
      } else {
        throw new Error(data.error || 'Registration failed');
      }
    } catch (err) {
      const msg = err.message || 'Failed to create account. Please try again.';
      if (msg.includes('already exists for this IRA number')) {
        setAccountAlreadyExists(true);
      }
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setError('');
    } else if (onBackToLogin) {
      onBackToLogin();
    }
  };

  if (registrationSuccess) {
    return (
      <div className="agent-registration">
        <div className="registration-container">
          <div className="registration-success">
            <div className="success-icon" aria-hidden="true">✓</div>
            <h2>Application successful</h2>
            <div className="success-message">
              <p className="success-headline">Your AgentBima account has been created.</p>
              <p><strong>Login details have been sent to:</strong></p>
              <p className="email-display">{email}</p>
              <p className="instructions">
                Check your inbox (and spam folder) for your username and password. You can log in and start using the portal.
                Please upload your IRA licence from Profile → KYC Verification.
              </p>
            </div>
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                if (onRegistrationSuccess) {
                  onRegistrationSuccess({
                    email: email.trim().toLowerCase(),
                    iraNumber: iraData.iraNumber,
                    agencyName: iraData.agencyName,
                  });
                } else if (onBackToLogin) {
                  onBackToLogin();
                }
              }}
            >
              Continue to Sign In
            </button>
          </div>
          <FlashMessage
            message="Application successful — welcome to AgentBima!"
            type="success"
            duration={10000}
            position="center"
          />
          {showSnackbar && (
            <Snackbar
              message={snackbarMessage}
              type={snackbarType}
              onClose={() => setShowSnackbar(false)}
              duration={0}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="agent-registration">
      <div className="registration-container">
        <div className="registration-header">
          <div className="registration-icon">👤</div>
          <h1>Agent Registration</h1>
          <p>Create your agent account in two simple steps</p>
        </div>

        <div className="step-indicator">
          <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <div className="step-number">1</div>
            <div className="step-label">Verify IRA</div>
          </div>
          <div className="step-connector" />
          <div className={`step ${step >= 2 ? 'active' : ''}`}>
            <div className="step-number">2</div>
            <div className="step-label">Your Details</div>
          </div>
        </div>

        {error && (
          <div className="error-message" role="alert">
            <span className="error-icon">⚠️</span>
            {error}
            {accountAlreadyExists && onBackToLogin && (
              <div className="registration-prompt" style={{ marginTop: '1rem' }}>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={onBackToLogin}
                >
                  Go to Login
                </button>
              </div>
            )}
          </div>
        )}

        {step === 1 ? (
          <form className="registration-form" onSubmit={handleVerifyIRA}>
            <div className="form-group">
              <label htmlFor="iraNumber">IRA License Number *</label>
              <input
                type="text"
                id="iraNumber"
                value={iraNumber}
                onChange={handleIRAInputChange}
                placeholder="e.g., IRA/05/36231/2025"
                required
                disabled={isLoading}
                autoFocus
                className={error ? 'error' : ''}
                autoComplete="off"
              />
              <small className="form-hint">
                Enter your Insurance Regulatory Authority (IRA) license number
              </small>
            </div>
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={handleBack} disabled={isLoading}>
                Back to Login
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isLoading || !iraNumber.trim()}
              >
                {isLoading ? (
                  <>
                    <span className="spinner" aria-hidden="true" />
                    Verifying...
                  </>
                ) : (
                  'Verify & Continue'
                )}
              </button>
            </div>
          </form>
        ) : (
          <form className="registration-form registration-form-details" onSubmit={handleCreateAccount}>
            {iraData && (
              <div className="verified-ira-info">
                <div className="info-item">
                  <span className="info-label">Agency</span>
                  <span className="info-value">{iraData.agencyName}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">IRA Number</span>
                  <span className="info-value">{iraData.iraNumber}</span>
                </div>
              </div>
            )}

            <KycLegalTypePicker
              legalType={kycLegalType}
              businessName={kycBusinessName}
              onLegalTypeChange={(value) => {
                setKycLegalType(value);
                setError('');
                if (value === 'business_entity' && !kycBusinessName && iraData?.agencyName) {
                  setKycBusinessName(iraData.agencyName);
                }
              }}
              onBusinessNameChange={(value) => {
                setKycBusinessName(value);
                setError('');
              }}
              disabled={isLoading}
            />

            <div className="form-group">
              <label htmlFor="kraPin">Tax PIN (KRA PIN) *</label>
              <input
                type="text"
                id="kraPin"
                value={kraPin}
                onChange={(e) => { setKraPin(e.target.value.trim().toUpperCase()); setError(''); }}
                placeholder="e.g. P051371296E"
                disabled={isLoading}
                autoFocus
                className={error ? 'error' : ''}
                autoComplete="off"
                maxLength={20}
              />
              <small className="form-hint">
                Enter your KRA Tax PIN as it appears on your tax registration
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                placeholder="your.email@example.com"
                required
                disabled={isLoading}
                className={error && !email.trim() ? 'error' : ''}
                autoComplete="email"
              />
              <small className="form-hint">
                Your login credentials will be sent to this email
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="idNumber">National ID *</label>
              <input
                type="text"
                id="idNumber"
                value={idNumber}
                onChange={(e) => { setIdNumber(e.target.value.replace(/\D/g, '').slice(0, 8)); setError(''); }}
                placeholder="e.g. 12345678"
                disabled={isLoading}
                inputMode="numeric"
                maxLength={8}
                autoComplete="off"
                required
                className={error && (!idNumber.trim() || idNumber.trim().length < 6 || idNumber.trim().length > 8) ? 'error' : ''}
              />
              <small className="form-hint">6–8 digits (Kenyan National ID)</small>
            </div>

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={handleBack} disabled={isLoading}>
                ← Back
              </button>
              <button
                type="submit"
                className="btn-primary btn-create-account"
                disabled={
                  isLoading ||
                  !kraPin.trim() ||
                  !email.trim() ||
                  !idNumber.trim() ||
                  idNumber.trim().length < 6 ||
                  idNumber.trim().length > 8 ||
                  kraPin.trim().length < 10
                }
              >
                {isLoading ? (
                  <>
                    <span className="spinner" aria-hidden="true" />
                    Creating Account...
                  </>
                ) : (
                  'Create Account'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
      {showSnackbar && (
        <Snackbar
          message={snackbarMessage}
          type={snackbarType}
          onClose={() => setShowSnackbar(false)}
          duration={snackbarType === 'success' ? 8000 : 0}
        />
      )}
    </div>
  );
};

export default AgentRegistration;
